// src/components/admin/UsersManager.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import Toolbar from './Toolbar';
import Column from './Column';
import EmployeeList from './EmployeeList';
import EmployeeEditor from './EmployeeEditor';
import NewEmployeeDialog from './NewEmployeeDialog';

import {
  fetchUsers,
  addEmployee,
  editEmployee,
  importUsers,
  exportUsers,
  refreshFromServer,
} from '../../services/usersApi';
import { useAuth } from "../../auth/AuthContext";
import "./UsersManager.css";
const UsersManager = () => {
  const { tenantServerUrl } = useAuth();
   const { primaryRestaurantId } = useAuth();
  
  const [data, setData] = useState({ employees: [] });
  const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const selectedEmployee = useMemo(
    () => data.employees.find(e => String(e.id) === String(selectedEmployeeId)) || null,
    [data, selectedEmployeeId]
  );

  const load = async () => {
    if (!tenantServerUrl) {
      setErr("No tenant server URL available");
      return;
    }
    setLoading(true);
     setErr(null);
    try {
      const m = await fetchUsers(tenantServerUrl);
      setData(m);
      if (m.employees.every(e => String(e.id) !== String(selectedEmployeeId))) {
        setSelectedEmployeeId(m.employees[0]?.id ?? null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    await refreshFromServer();
    await load();
  };

  const handleImport = async (jsonText) => {
    try { await importUsers(jsonText); await load(); alert('Employee import complete'); }
    catch (e) { alert(`Import failed: ${e.message}`); }
  };

  const handleExport = async () => {
    const json = await exportUsers();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'employees.json'; a.click();
    URL.revokeObjectURL(url);
  };

  // NEW: open modal
  const handleAddEmployee = () => setShowNew(true);

  // NEW: when dialog succeeds, refresh + select
  const onCreated = async (emp) => {
    await load();
    if (emp?.id) setSelectedEmployeeId(emp.id);
  };

  const handleEditEmployeeQuick = async () => {
    if (!selectedEmployee) return alert('Select an employee first');
    const newName = prompt('Full name:', selectedEmployee.display_name || '');
    if (newName == null) return;
    await editEmployee(selectedEmployee.id, {
      display_name: newName,
      username: selectedEmployee.username,
      bywho: localStorage.getItem('loggedUser_Username')
          || localStorage.getItem('loggedUser_Email')
          || 'admin',
    });
    await load();
  };

  const usersManagerEditorRef = useRef(null);
  const [isToolbarMinimized, setToolbarMinimized] = useState(false);
  const scrollThreshold = 100;
  const onScroll = useCallback(() => {
    const y = typeof window !== 'undefined' ? window.scrollY ?? document.documentElement.scrollTop : 0;
    setToolbarMinimized(y > scrollThreshold);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);
  useEffect(() => {
    if (selectedEmployeeId && selectedEmployee && usersManagerEditorRef.current) {
      usersManagerEditorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const timer = setTimeout(() => window.scrollBy({ top: -75, behavior: 'smooth' }), 400);
      return () => clearTimeout(timer);
    }
  }, [selectedEmployeeId, selectedEmployee]);

  return (
    <div className="users-manager-root">
      <div className={`users-manager-toolbar ${isToolbarMinimized ? 'users-manager-toolbar--minimized' : ''}`}>
        <Toolbar
          onRefresh={handleRefresh}
          onImport={handleImport}
          onExport={handleExport}
          onAddCategory={handleAddEmployee}
          onEditCategory={handleEditEmployeeQuick}
          onAddSubcategory={null}
        />
        <button
          type="button"
          className="users-manager-toolbar-expand"
          aria-label="Expand user actions"
          onClick={() => setToolbarMinimized(false)}
        >
          ▼ Show actions
        </button>
      </div>

      <div className={`users-manager-content ${isToolbarMinimized ? 'users-manager-content--toolbar-minimized' : ''}`}>
      <div style={{ padding: 10, fontWeight: 'bold' }}>
        Selected: {selectedEmployee ? (selectedEmployee.display_name || selectedEmployee.username) : 'None'}
        {loading && <span style={{ marginLeft: 10, fontWeight: 'normal', opacity: .6 }}>Loading…</span>}
      </div>

      <div className="users-manager-layout" style={{
        display: 'grid',
        gridTemplateColumns: '450px 1fr',
        gap: 12,
        background: '#f1f1f1',
        padding: 12,
        minHeight: 'calc(50vh - 110px)'
      }}>
        <Column>
          <EmployeeList
            employees={data.employees}
            selectedEmployeeId={selectedEmployeeId}
            onSelect={(e) => setSelectedEmployeeId(e.id)}
            onOpen={(e) => setSelectedEmployeeId(e.id)}
          />
        </Column>

        <div
          ref={usersManagerEditorRef}
          id="users-manager-editor"
          className="users-manager-editor"
          style={{
            background: '#fff',
            border: '1px solid #e6e6e6',
            borderRadius: 12,
            padding: 12,
            overflow: 'auto'
          }}>
          {selectedEmployee ? (
            <EmployeeEditor
              employee={selectedEmployee}
              restaurantId={primaryRestaurantId}
              onCancel={() => setSelectedEmployeeId(null)}
              onSaved={async () => { await load(); }}
              onChanged={async () => { await load(); }}
              onDeleted={async () => { await load(); setSelectedEmployeeId(null); }}
            />
          ) : (
            <div style={{ color: '#777' }}>Select an employee to edit.</div>
          )}
        </div>
      </div>
      </div>

      {/* New Employee modal */}
      <NewEmployeeDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={onCreated}
        restaurantId="1"
      />
    </div>
  );
};

export default UsersManager;
